/**
 * Biome kit (WORKBOARD E5; owner, Oct 9: "more tree types, similar with rocks — relatively quotidian
 * but with a little intrigue / variation that's on brand", the brand a muted Astroneer × Ravenfield
 * mix): new tree species and rock types for biomes, as stand-ins against the style sheet.
 *
 * The look: faceted, rounded clumps and simple cones (Astroneer), low-poly and cheap to draw in
 * crowds (Ravenfield), matte and muted. Mostly ordinary — a broadleaf, a birch, a fir, a wind-bent
 * pine, a bottle tree, a dead snag; shelf slabs, an erratic boulder, basalt columns, a hoodoo — with
 * a little intrigue: the lantern fir's pale beads, the veined stone's faint seam.
 *
 * Model units match the game's flora and rocks (a tree ~0.3 tall at scale 1), so a species draws
 * at either size class: **giant** through today's path (TREE_DRAW_SCALE × the planet's prop scale —
 * a landmark), **normal** at NORMAL_TREE_SCALE, world-absolute (about six people at k 1/20 —
 * cover, in stands). Colours come from the biome (foliage, bark) or the planet (rock). Seeded:
 * peers draw the same tree. Presentation only; nothing in the sim reads these yet.
 *
 * Design reference: design_sheets/biomes.html
 */
import * as THREE from 'three';
import { markShared } from './meshBake';
import { buildFamilyTree, seg, type TreeFamily, type V3 } from './treeFamilies';
import type { BiomeId, FloraType } from '../types';
import { TREE_TRUNK_SIZE, NORMAL_TREE_PERSONS, NORMAL_ROCK_PERSONS } from '../constants';
import { PERSON_BODY_HEIGHT } from '../core/scaleProfile';
import { biomeDressing } from '../core/biomeDressing';

export type TreeSpecies = 'puffElm' | 'paleBirch' | 'stackFir' | 'windPine' | 'bottleTree' | 'snag' | 'lanternFir';
export type RockKind = 'slab' | 'erratic' | 'columns' | 'hoodoo' | 'veined' | 'tor' | 'shard' | 'cobble'
  // 74.4 (DECISIONS 351): four new kinds (design_sheets/dressing.html) and the termite mound (a dressing piece)
  | 'kopje' | 'ledge' | 'split' | 'crystal' | 'mound';
export const TREE_SPECIES: TreeSpecies[] = ['puffElm', 'paleBirch', 'stackFir', 'windPine', 'bottleTree', 'snag', 'lanternFir'];
export const ROCK_KINDS: RockKind[] = ['slab', 'erratic', 'columns', 'hoodoo', 'veined', 'tor', 'shard', 'cobble', 'kopje', 'ledge', 'split', 'crystal', 'mound'];

/** Model units → world units for the normal size class at k 1/20 (constants NORMAL_TREE_PERSONS × a person): a ~0.3-unit tree stands ~0.33 u, about six people. */
export const NORMAL_TREE_SCALE = NORMAL_TREE_PERSONS * PERSON_BODY_HEIGHT / 20;
/** …a kit rock ~0.2 tall stands ~0.09 u (a person and a half): cover, not a cliff. */
export const NORMAL_ROCK_SCALE = NORMAL_ROCK_PERSONS * PERSON_BODY_HEIGHT / 20;
/** …and ground flora (grass, reeds, lichen) waist-high around a person. */
export const NORMAL_GROUND_SCALE = 0.3;

/**
 * Trunks (model units at scale 1): radius and height — what a bullet, a walker and the eye stop at;
 * foliage never blocks (the game's rule, TREE_TRUNK_SIZE). The bottle tree is the thick one; firs
 * are short-trunked under their skirts; obstacles are upright cylinders, so the wind pine's leaning
 * trunk counts only its low part (the part that stays inside one).
 */
export const KIT_TRUNK: Record<TreeSpecies, { half: number; height: number }> = Object.fromEntries(
  ['puffElm', 'paleBirch', 'stackFir', 'windPine', 'bottleTree', 'snag', 'lanternFir'].map(sp => [sp, TREE_TRUNK_SIZE[sp]]),
) as Record<TreeSpecies, { half: number; height: number }>;   // the obstacle table's rows (constants TREE_TRUNK_SIZE)

export interface TreeColors { bark: string; leaf: string; leafDark: string; accent?: string }

/** Each biome's tree colours (core/biomes.ts holds the biome itself; colour is presentation). Muted, one family each. */
export const BIOME_COLORS: Record<BiomeId, TreeColors> = {
  broadleaf: { bark: '#5a4a3a', leaf: '#6f8a4c', leafDark: '#4e6638', accent: '#d6d2c4' },
  firStand: { bark: '#4a3c30', leaf: '#45604c', leafDark: '#324a3c' },
  heath: { bark: '#5c5048', leaf: '#66704a', leafDark: '#4c5638' },
  savanna: { bark: '#7a6450', leaf: '#8a8650', leafDark: '#6a683e' },
  ashBarrens: { bark: '#3e3632', leaf: '#5e5038', leafDark: '#463c2c' },
  lanternGrove: { bark: '#3a3a40', leaf: '#3e5660', leafDark: '#2c4048', accent: '#e6d89a' },
  // owner, Oct 10: the four from the same kit
  birchGlade: { bark: '#d8d2c4', leaf: '#a4bc6e', leafDark: '#7e9852', accent: '#f0ebe0' },   // light: white bark, bright leaf
  burn: { bark: '#2b2724', leaf: '#4a403a', leafDark: '#332c28' },                             // charred; the survivor fir keeps a little green in the dark
  scrub: { bark: '#8a7a62', leaf: '#8f9460', leafDark: '#6e7448' },                             // dusty olive
  hollowWood: { bark: '#4a4a58', leaf: '#6a7e8c', leafDark: '#4e5e6a', accent: '#bfe6ec' },     // sonora: slate and pale glass
};
/** Each biome's ground cover, drawn at a tree's foot (decoration: never an obstacle, never in state) — its dressing's `cover` (74.4). */
export const BIOME_GROUND: Record<BiomeId, FloraType[]> = Object.fromEntries(Object.keys(BIOME_COLORS).map(b => [b, biomeDressing(b as BiomeId).cover])) as Record<BiomeId, FloraType[]>;
/** Ground cover's size beside a normal tree (its model units, relative to the tree's): reeds stay knee-high, mats and lichen ankle-to-shin. */
export const GROUND_TUFT_SCALE: Partial<Record<FloraType, number>> = { ghostReed: 0.1, velvetMatgrass: 0.2, thermocapLichen: 0.2, lanternBracket: 0.16 };
export interface KitModel { group: THREE.Group; sway: THREE.Object3D[] }

// ---------------------------------------------------------------------------
// Parts
// ---------------------------------------------------------------------------

const matCache = new Map<string, THREE.MeshLambertMaterial>();
function mat(color: string, glow = 0): THREE.MeshLambertMaterial {
  const key = `${color}:${glow}`;
  let m = matCache.get(key);
  if (!m) {
    m = markShared(new THREE.MeshLambertMaterial({ color, flatShading: true, ...(glow > 0 ? { emissive: new THREE.Color(color), emissiveIntensity: glow } : {}) }));
    matCache.set(key, m);
  }
  return m;
}
/** mulberry32: well mixed from the first draw (a bare xorshift's consecutive draws correlate, which skewed the picks). */
function rng(seed: number): () => number {
  let s = (seed * 2654435761) >>> 0;
  return () => {
    s = (s + 0x6d2b79f5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** Jitter a geometry's vertices by a hash of their position (shared corners agree: the mesh stays closed). */
function lumpy(geo: THREE.BufferGeometry, amount: number, offset: number): THREE.BufferGeometry {
  const pos = geo.attributes.position as THREE.BufferAttribute;
  for (let i = 0; i < pos.count; i++) {
    const x = pos.getX(i), y = pos.getY(i), z = pos.getZ(i);
    const h = Math.sin(x * 127.1 + y * 311.7 + z * 74.7 + offset) * 43758.5453;
    const j = 1 + amount * ((h - Math.floor(h)) - 0.5) * 2;
    pos.setXYZ(i, x * j, y * j, z * j);
  }
  geo.computeVertexNormals();
  return geo;
}

// ---------------------------------------------------------------------------
// Trees (model units: ~0.25–0.4 tall at scale 1, base at y = 0)
// ---------------------------------------------------------------------------

/**
 * Build a tree of `species` in a biome's colours, at model scale `scale` (0.8–1.2 variety), seeded — 70.2: the redrawn
 * families (treeFamilies.ts), the same as the stands draw at the normal size.
 */
export function buildTree(species: TreeFamily, colors: TreeColors, scale: number, seed: number): KitModel {
  const { group: g, sway } = buildFamilyTree(species, colors, seed);
  g.scale.setScalar(scale);
  g.rotation.y = rng(seed ^ 0x5bd1e995)() * Math.PI * 2;
  return { group: g, sway };
}

// ---------------------------------------------------------------------------
// Rocks (model units: ~0.12–0.35 tall at scale 1, base at y = 0; coloured by the planet's rock)
// ---------------------------------------------------------------------------

function shade(hex: string, k: number): string { return '#' + new THREE.Color(hex).multiplyScalar(k).getHexString(); }

/** Build a rock of `kind` in the planet's rock colour, seeded. `accent` colours the veined stone's seam. */
export function buildRock(kind: RockKind, color: string, scale: number, seed: number, accent = '#cfe8f0'): THREE.Group {
  const g = new THREE.Group();
  const r = rng(seed);
  if (kind === 'slab') {
    // shelves of sedimentary stone, each a little offset and a shade apart
    let y = 0;
    for (let i = 0; i < 3; i++) {
      const w = 0.32 - i * 0.07, h = 0.05 + r() * 0.02, d = 0.22 - i * 0.04;
      const m = new THREE.Mesh(lumpy(new THREE.BoxGeometry(w, h, d, 2, 1, 2), 0.06, r() * 9), mat(shade(color, 0.92 + i * 0.07)));
      m.position.set((r() - 0.5) * 0.04, y + h / 2, (r() - 0.5) * 0.03); m.rotation.y = (r() - 0.5) * 0.4;
      g.add(m); y += h * 0.92;
    }
  } else if (kind === 'erratic') {
    // one smooth boulder, dropped by ice: rounder and less jagged than the game's rocks
    const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.13, 2), 0.05, r() * 9), mat(shade(color, 1.08)));
    m.scale.set(1.15, 0.8, 0.95); m.position.y = 0.09; g.add(m);
  } else if (kind === 'columns') {
    // columnar basalt: hexagonal prisms, tallest in the middle
    const n = 7;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.3, rad = i === 0 ? 0 : 0.055 + r() * 0.02;
      const h = (i === 0 ? 0.3 : 0.12 + r() * 0.14);
      const m = new THREE.Mesh(new THREE.CylinderGeometry(0.03, 0.032, h, 6), mat(shade(color, 0.85 + r() * 0.2)));
      m.position.set(Math.cos(a) * rad, h / 2, Math.sin(a) * rad); m.rotation.y = r();
      g.add(m);
    }
  } else if (kind === 'tor') {
    // a granite tor: three big rounded blocks stacked and offset, the top one smallest (a mountain rock)
    let y = 0;
    for (let i = 0; i < 3; i++) {
      const rr = 0.15 - i * 0.033;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.08, r() * 9), mat(shade(color, 0.95 + i * 0.06)));
      m.scale.set(1.25, 0.68, 1.05); m.position.set((r() - 0.5) * 0.06, y + rr * 0.72, (r() - 0.5) * 0.05); m.rotation.y = r() * 0.8;
      g.add(m); y += rr * 1.3;
    }
  } else if (kind === 'shard') {
    // a standing shard: a tall angular slab tilted a little, one edge catching the light (a mountain rock)
    const m = new THREE.Mesh(lumpy(new THREE.BoxGeometry(0.1, 0.42, 0.22, 1, 3, 2), 0.1, r() * 9), mat(shade(color, 1.0)));
    m.position.set(0, 0.19, 0); m.rotation.set((r() - 0.5) * 0.3, r() * Math.PI, 0.18 + r() * 0.15);
    g.add(m);
    const foot = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.09, 1), 0.12, r() * 9), mat(shade(color, 0.85)));
    foot.scale.set(1.3, 0.5, 1.1); foot.position.set(0.04, 0.03, 0.02); g.add(foot);
  } else if (kind === 'cobble') {
    // a cobble cluster: four or five small rounded boulders nested together (a field rock)
    const n = 4 + Math.floor(r() * 2);
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.5, rad = i === 0 ? 0 : 0.075 + r() * 0.03, rr = 0.065 + r() * 0.03;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.1, r() * 9), mat(shade(color, 0.9 + r() * 0.2)));
      m.scale.set(1.1, 0.75, 1); m.position.set(Math.cos(a) * rad, rr * 0.7, Math.sin(a) * rad); m.rotation.y = r() * 3;
      g.add(m);
    }
  } else if (kind === 'hoodoo') {
    // a weathered pillar of stacked lumps with a darker cap stone
    let y = 0;
    for (let i = 0; i < 4; i++) {
      const rr = 0.06 - i * 0.008 + (r() - 0.5) * 0.01;
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.1, r() * 9), mat(shade(color, 1 + (i % 2) * 0.08)));
      m.scale.y = 1.1; m.position.y = y + rr; g.add(m); y += rr * 1.7;
    }
    const cap = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.06, 1), 0.08, r() * 9), mat(shade(color, 0.7)));
    cap.scale.set(1.2, 0.45, 1.1); cap.position.y = y + 0.02; g.add(cap);
  } else if (kind === 'kopje') {
    // 74.4: rounded granite boulders piled, one perched on top (standing cover, a lookout on top)
    const stone = (geo: THREE.BufferGeometry, k: number, x: number, y: number, z: number) => { const m = new THREE.Mesh(geo, mat(shade(color, k))); m.position.set(x, y, z); g.add(m); return m; };
    for (const [x, rr, z] of [[-0.08, 0.13, 0], [0.09, 0.12, 0.03], [0.0, 0.1, -0.1]] as const) {
      const m = stone(lumpy(new THREE.IcosahedronGeometry(rr, 1), 0.07, r() * 9), 0.92 + r() * 0.14, x, rr * 0.62, z);
      m.scale.set(1.2, 0.74, 1.05); m.rotation.y = r() * 3;
    }
    const top = stone(lumpy(new THREE.IcosahedronGeometry(0.1, 1), 0.06, r() * 9), 1.08, 0.01, 0.22, -0.01); top.scale.set(1.15, 0.82, 1); top.rotation.set(0.15, r() * 3, 0.1);
    const perch = stone(lumpy(new THREE.IcosahedronGeometry(0.055, 1), 0.08, r() * 9), 1.0, 0.03, 0.33, 0.0); perch.scale.set(1.1, 0.85, 1);
  } else if (kind === 'ledge') {
    // 74.4: a band of bedrock breaking the surface, long and low (knee-high: cover lying behind it)
    let x = -0.24;
    for (let i = 0; i < 3; i++) {
      const w = 0.17 + r() * 0.05, h = 0.07 + r() * 0.03, d = 0.11 + r() * 0.03;
      const m = new THREE.Mesh(lumpy(new THREE.BoxGeometry(w, h, d, 3, 1, 2), 0.07, r() * 9), mat(shade(color, 0.9 + i * 0.06)));
      m.position.set(x + w / 2, h / 2 - 0.004, (r() - 0.5) * 0.04); m.rotation.set((r() - 0.5) * 0.12, (r() - 0.5) * 0.25, (r() - 0.5) * 0.1);
      g.add(m); x += w * 0.88;
    }
  } else if (kind === 'split') {
    // 74.4: an erratic frost-split in two, the halves leaning apart (the slit is drawn; the sim's rock is one profile)
    for (const side of [-1, 1]) {
      const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.135, 2), 0.05, r() * 9), mat(shade(color, side < 0 ? 1.04 : 0.96)));
      m.position.set(side * 0.052, 0.085, 0); m.scale.set(0.5, 0.78, 0.95); m.rotation.z = -side * 0.08;
      g.add(m);
    }
  } else if (kind === 'crystal') {
    // 74.4: pale hexagonal prisms standing out of dark stone, faintly lit (the odd one, as the veined stone)
    const base = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.1, 1), 0.12, r() * 9), mat(shade(color, 0.85)));
    base.position.y = 0.04; base.scale.set(1.3, 0.55, 1.1); g.add(base);
    const glass = '#bfe6ec', n = 6;
    for (let i = 0; i < n; i++) {
      const a = (i / n) * Math.PI * 2 + r() * 0.5, rad = i === 0 ? 0 : 0.04 + r() * 0.03, h = i === 0 ? 0.28 : 0.1 + r() * 0.12, w = i === 0 ? 0.026 : 0.014 + r() * 0.008;
      const tilt = i === 0 ? 0.05 : 0.25 + r() * 0.25, at: V3 = [Math.cos(a) * rad, 0.03, Math.sin(a) * rad];
      const end: V3 = [at[0] + Math.cos(a) * Math.sin(tilt) * h, at[1] + Math.cos(tilt) * h, at[2] + Math.sin(a) * Math.sin(tilt) * h];
      const prism = seg(g, w, w, at, end, glass, 6); prism.material = mat(glass, 0.25);
      const tip = new THREE.Mesh(new THREE.ConeGeometry(w, w * 2.2, 6), mat(glass, 0.35));
      tip.position.set(...end); tip.quaternion.copy(prism.quaternion); tip.translateY(w * 1.1);
      g.add(tip);
    }
  } else if (kind === 'mound') {
    // 74.4's termite mound: a clay spire two people tall with two lesser spires (standing cover: a rock's tables); its own clay
    const P = 1 / NORMAL_ROCK_PERSONS, clay = '#9a7a58';
    g.add(new THREE.Mesh(lumpy(new THREE.LatheGeometry([[0.9, 0], [0.8, 0.5], [0.6, 1.1], [0.42, 1.6], [0.26, 2.0], [0.1, 2.25]].map(([x, y]) => new THREE.Vector2(x * P, y * P)), 7), 0.08, r() * 9), mat(clay)));
    for (let k = 0; k < 2; k++) {
      const a = r() * Math.PI * 2, m = new THREE.Mesh(lumpy(new THREE.ConeGeometry(0.25 * P, (0.9 + r() * 0.4) * P, 6), 0.1, r() * 9), mat(shade(clay, 0.92)));
      m.position.set(Math.cos(a) * 0.55 * P, 0.5 * P, Math.sin(a) * 0.55 * P); m.rotation.set((r() - 0.5) * 0.3, 0, (r() - 0.5) * 0.3); g.add(m);
    }
  } else {
    // a plain angular stone with one thin, faintly glowing seam across it — the intrigue
    const m = new THREE.Mesh(lumpy(new THREE.IcosahedronGeometry(0.12, 0), 0.15, r() * 9), mat(shade(color, 1.05)));
    m.scale.set(1.1, 0.75, 1); m.position.y = 0.08; g.add(m);
    const seam = new THREE.Mesh(new THREE.BoxGeometry(0.2, 0.008, 0.012), mat(accent, 0.7));
    seam.position.set(0, 0.09, 0.075); seam.rotation.set(0.2, 0.15, 0.25); g.add(seam);
  }
  shadeRock(g);
  g.scale.setScalar(scale);
  g.rotation.y = r() * Math.PI * 2;
  return g;
}

const vmatCache = new Map<string, THREE.MeshLambertMaterial>();
/** The rock's material with vertex colours on (the shade multiplies the colour); cached per colour and glow like `mat`. */
function vmat(from: THREE.MeshLambertMaterial): THREE.MeshLambertMaterial {
  const key = `${from.color.getHexString()}:${from.emissiveIntensity}`;
  let m = vmatCache.get(key);
  if (!m) { m = markShared(from.clone()); m.vertexColors = true; vmatCache.set(key, m); }
  return m;
}
/**
 * Owner, Oct 10 ("improving rocks"): a rock reads as a rock when its top is lit and its base is in
 * the dirt — vertex colours shade each stone from 0.72 at the bottom to 1.12 at the top, with a
 * little per-face jitter so the facets differ. Zero runtime cost; the geometry stays one mesh.
 */
export function shadeRock(g: THREE.Object3D): void {
  g.traverse(o => {
    const m = o as THREE.Mesh;
    if (!m.isMesh || !(m.material as THREE.MeshLambertMaterial).isMeshLambertMaterial) return;
    const mat0 = m.material as THREE.MeshLambertMaterial;
    if (mat0.emissive.getHex() !== 0) return;                                // the veined stone's seam keeps its glow flat (Lambert's emissiveIntensity is 1 even when the emissive is black)
    const geo = m.geometry;
    geo.computeBoundingBox();
    const lo = geo.boundingBox!.min.y, hi = geo.boundingBox!.max.y, span = Math.max(1e-6, hi - lo);
    const pos = geo.attributes.position as THREE.BufferAttribute, col = new Float32Array(pos.count * 3);
    for (let i = 0; i < pos.count; i++) {
      const t = (pos.getY(i) - lo) / span;
      const face = Math.floor(i / 3), h = Math.sin(face * 12.9898 + 78.233) * 43758.5453, jitter = 0.96 + 0.08 * (h - Math.floor(h));
      const shade = (0.72 + 0.4 * t) * jitter;
      col[i * 3] = shade; col[i * 3 + 1] = shade; col[i * 3 + 2] = shade;
    }
    geo.setAttribute('color', new THREE.BufferAttribute(col, 3));
    m.material = vmat(mat0);
  });
}
